import { Circle, Divider, Flex, Stack, StackProps, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { Fragment, useMemo } from 'react';

import { FlightSegment, useFlightSegmentsHelper } from '../../hooks/useFlightSegmentsHelper';

type Props = StackProps & {
  flightSegments: FlightSegment[];
  pickupOrigin?: string;
};

export const AirportStops = ({ flightSegments = [], pickupOrigin, ...rest }: Props) => {
  const { airports } = useFlightSegmentsHelper(flightSegments);

  const stops = useMemo(() => {
    const airportStops = airports.map((airport) => ({ id: airport?.id }));
    if (!pickupOrigin) return airportStops;
    return [{ id: pickupOrigin }, ...airportStops];
  }, [airports, pickupOrigin]);

  const renderStop = (stop: { id: string }, index: number) => {
    const isFirst = index === 0;
    const isLast = index === stops.length - 1;
    const isSecondToLast = index === stops.length - 2;

    return (
      <Fragment key={`${stop.id}-${index}`}>
        <Flex>
          <Text textStyle="bodySmallRegular" color="text.subtle" fontWeight={isFirst || isLast ? 'bold' : 'normal'}>
            {stop.id}
          </Text>
        </Flex>
        <Show if={!isLast}>
          <Stack direction="row" align="center" spacing="-1" flex={1}>
            <Show if={isFirst || isLast}>
              <Circle size="8px" bg="transparent" border="2px" borderColor="border.default" mr="-0.5" />
            </Show>
            <Divider borderColor="border.default" opacity="1" borderWidth="1px" />
            <Show if={isSecondToLast}>
              <Circle size="8px" bg="transparent" border="2px" ml="-0.5" borderColor="border.default" fontSize="sm" />
            </Show>
          </Stack>
        </Show>
      </Fragment>
    );
  };

  return (
    <Stack direction="row" align="center" spacing={1} w="full" {...rest}>
      {stops.map(renderStop)}
    </Stack>
  );
};
